import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { postContact, getCollegeInfo } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    getCollegeInfo().then(setInfo).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please provide name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await postContact(form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Unable to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Get in Touch"
        title="We'd love to hear from you."
        subtitle="Have a question about admissions, programs, or campus life? Reach out — our team is here to help."
      />

      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Info */}
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: MapPin, title: "Address", text: info?.address || "Mian Bazar, Gorakhpur – 273001 (U.P. – INDIA)" },
              { icon: Phone, title: "Main Office", text: `${info?.phone || "+91-551-4052322"} · ${info?.phone_hours || "10 a.m. – 4 p.m."}` },
              { icon: Phone, title: "Principal", text: info?.principal_phone || "+91-98394-17577" },
              { icon: Mail, title: "Email", text: info?.email || "principaljasigpgcollegegkp@gmail.com" },
              { icon: Clock, title: "Office Hours", text: "Mon – Sat · 10:00 a.m. – 4:00 p.m." },
            ].map((i) => (
              <div
                key={i.title}
                className="bg-white rounded-3xl p-6 border border-brand-primary/10 flex gap-4"
                data-testid={`info-${i.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-11 h-11 rounded-full bg-brand-accent text-brand-primary flex items-center justify-center flex-shrink-0">
                  <i.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-brand-muted mb-1">
                    {i.title}
                  </div>
                  <div className="text-brand-ink font-medium">{i.text}</div>
                </div>
              </div>
            ))}

            <div className="rounded-3xl overflow-hidden border border-brand-primary/10 h-64 lg:h-72 bg-brand-accent" data-testid="map-embed">
              <iframe
                title="College Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3562.8861588977647!2d83.3600446!3d26.7480087!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3991446c0a555555%3A0x2f9bc77dc9191ffa!2sJawad%20Ali%20Shah%20Imambara%20Girls%20Postgraduate%20College!5e0!3m2!1sen!2sin!4v1778207982327!5m2!1sen!2sin"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white rounded-3xl p-6 lg:p-10 border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.06)] space-y-5"
          >
            <div>
              <div className="eyebrow mb-2">Send a message</div>
              <h3 className="font-serif text-2xl lg:text-3xl text-brand-primary">
                Let's talk.
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name">Your Name *</Label>
                <Input id="c-name" name="name" data-testid="c-name" value={form.name} onChange={onChange} placeholder="Full name" required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="c-email">Email *</Label>
                <Input id="c-email" name="email" type="email" data-testid="c-email" value={form.email} onChange={onChange} placeholder="you@example.com" required className="mt-1" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-phone">Phone</Label>
                <Input id="c-phone" name="phone" data-testid="c-phone" value={form.phone} onChange={onChange} placeholder="+91" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="c-subject">Subject</Label>
                <Input id="c-subject" name="subject" data-testid="c-subject" value={form.subject} onChange={onChange} placeholder="e.g. Admission enquiry" className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="c-message">Message *</Label>
              <Textarea id="c-message" name="message" data-testid="c-message" value={form.message} onChange={onChange} placeholder="How can we help you?" rows={5} required className="mt-1" />
            </div>
            <button
              type="submit"
              data-testid="c-submit"
              disabled={submitting}
              className="btn-brand disabled:opacity-60"
            >
              {submitting ? "Sending..." : (
                <>Send Message <Send className="w-4 h-4" /></>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
